import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AtSign, Check, CheckCheck, ChevronDown, Download, Eye, FileText, Infinity, Minus, Paperclip, Send, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useGarageWS } from "../hooks/useWebSocket";
import { loopState } from "../lib/subscriptionModules";

const STAFF_ROLES = new Set(["admin", "tenant_admin", "branch_admin", "reception", "mechanic", "tester", "shopkeeper"]);
const BRAND_ORANGE = "#F26A21";

const safeName = (name = "") => name.trim() || "Team member";

const availability = (member = {}) => {
  if (member.active) return { dot: "bg-emerald-400" };
  if (member.seen_today) return { dot: "bg-yellow-400" };
  return { dot: "bg-red-500" };
};

const readBy = (message = {}) => Array.isArray(message.read_by) ? message.read_by.map(String) : [];

const localToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const messageTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const fileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const downloadBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name || "attachment";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const playLoopTone = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const master = ctx.createGain();
    const tone = ctx.createBiquadFilter();
    const now = ctx.currentTime;
    tone.type = "bandpass";
    tone.frequency.setValueAtTime(720, now);
    tone.Q.setValueAtTime(3.4, now);
    master.gain.setValueAtTime(0.001, now);
    master.gain.exponentialRampToValueAtTime(0.42, now + 0.018);
    master.gain.exponentialRampToValueAtTime(0.001, now + 0.68);
    tone.connect(master);
    master.connect(ctx.destination);

    [0, 0.16].forEach((offset) => {
      const osc = ctx.createOscillator();
      osc.type = "square";
      osc.frequency.setValueAtTime(560, now + offset);
      osc.frequency.exponentialRampToValueAtTime(920, now + offset + 0.06);
      osc.frequency.exponentialRampToValueAtTime(430, now + offset + 0.22);
      osc.connect(tone);
      osc.start(now + offset);
      osc.stop(now + offset + 0.28);
    });

    [0.04, 0.2].forEach((offset) => {
      const overtone = ctx.createOscillator();
      overtone.type = "triangle";
      overtone.frequency.setValueAtTime(1180, now + offset);
      overtone.frequency.exponentialRampToValueAtTime(780, now + offset + 0.18);
      overtone.connect(tone);
      overtone.start(now + offset);
      overtone.stop(now + offset + 0.2);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1100);
  } catch (_) {}
};

export default function InternalChatWidget() {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [staff, setStaff] = useState([]);
  const [text, setText] = useState("");
  const [recipientId, setRecipientId] = useState("all");
  const [open, setOpen] = useState(() => localStorage.getItem("loop_chat_open") !== "false");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [position, setPosition] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("loop_chat_position") || "null") || { right: 24, bottom: 24 };
    } catch (_) {
      return { right: 24, bottom: 24 };
    }
  });
  const dragRef = useRef(null);
  const suppressOpenRef = useRef(false);
  const listRef = useRef(null);
  const fileRef = useRef(null);

  const loop = loopState(user);
  const isStaff = !!user && STAFF_ROLES.has(user.role) && loop.enabled;
  const today = localToday();

  const canReadMessage = useCallback((message) => {
    if (!message) return false;
    if (!message.scope || message.scope === "ALL") return true;
    return message.sender_id === user?.id || message.recipient_id === user?.id;
  }, [user?.id]);

  const appendMessage = useCallback((message) => {
    if (!message?.id || !canReadMessage(message)) return;
    setMessages((current) => {
      if (current.some((item) => item.id === message.id)) return current;
      return [...current, message].slice(-150);
    });
  }, [canReadMessage]);

  const loadChat = useCallback(async () => {
    if (!isStaff) return;
    const [msgRes, staffRes] = await Promise.all([
      api.get("/chat/messages"),
      api.get("/chat/staff"),
    ]);
    setMessages(Array.isArray(msgRes.data) ? msgRes.data : []);
    setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
  }, [isStaff]);

  const { connected } = useGarageWS(isStaff ? token : null, (event) => {
    if (event.type === "CHAT_MESSAGE") {
      appendMessage(event.data);
      if (event.data?.sender_id !== user?.id) playLoopTone();
    }
    if (event.type === "CHAT_PING" && event.data?.sender_id !== user?.id) {
      // CHAT_MESSAGE already plays the receive sound; ping only carries mention metadata.
    }
    if (event.type === "CHAT_PRESENCE") {
      api.get("/chat/staff").then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
    if (event.type === "CHAT_READ") {
      const ids = Array.isArray(event.data?.message_ids) ? event.data.message_ids.map(String) : [];
      const readerId = String(event.data?.reader_id || "");
      if (!ids.length || !readerId) return;
      setMessages((current) => current.map((message) => {
        if (!ids.includes(String(message.id))) return message;
        const nextReadBy = Array.from(new Set([...readBy(message), readerId]));
        return { ...message, read_by: nextReadBy };
      }));
    }
  });

  useEffect(() => {
    loadChat().catch(() => {});
  }, [loadChat]);

  useEffect(() => {
    localStorage.setItem("loop_chat_open", String(open));
  }, [open]);

  useEffect(() => {
    localStorage.setItem("loop_chat_position", JSON.stringify(position));
  }, [position]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview?.url]);

  const chatTargets = useMemo(() => staff.filter((member) => member.id !== user?.id), [staff, user?.id]);
  const selectedRecipient = useMemo(
    () => staff.find((member) => member.id === recipientId),
    [staff, recipientId]
  );
  const displayedMessages = useMemo(
    () => messages.filter((message) => (!message.day || message.day === today) && canReadMessage(message)),
    [canReadMessage, messages, today]
  );
  const unreadMessages = useMemo(
    () => displayedMessages.filter((message) => message.sender_id !== user?.id && !readBy(message).includes(String(user?.id || ""))),
    [displayedMessages, user?.id]
  );
  const unreadCount = unreadMessages.length;

  const mentionIds = useMemo(() => {
    const lowered = text.toLowerCase();
    const compact = lowered.replace(/\s+/g, "");
    return staff
      .filter((member) => {
        const name = safeName(member.name).toLowerCase();
        const compactName = name.replace(/\s+/g, "");
        return lowered.includes(`@${name}`) || compact.includes(`@${compactName}`);
      })
      .map((member) => member.id);
  }, [staff, text]);

  const send = async (event) => {
    event.preventDefault();
    const message = text.trim();
    if ((!message && attachments.length === 0) || sending || uploading) return;
    setSending(true);
    setText("");
    const filesToSend = attachments;
    setAttachments([]);
    try {
      const response = await api.post("/chat/messages", {
        message,
        attachments: filesToSend,
        mentions: mentionIds,
        recipient_id: recipientId === "all" ? null : recipientId,
      });
      if (!connected) appendMessage(response.data);
      playLoopTone();
    } finally {
      setSending(false);
    }
  };

  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files.slice(0, 4)) {
        const form = new FormData();
        form.append("file", file);
        const response = await api.post("/chat/files", form, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((current) => [...current, response.data].slice(0, 4));
      }
    } finally {
      setUploading(false);
    }
  };

  const downloadAttachment = async (file) => {
    if (!file?.id) return;
    try {
      setDownloadError("");
      const response = await fetchAttachment(file);
      downloadBlob(response.data, file.name || "attachment");
    } catch (_) {
      setDownloadError("Could not download attachment. Please try again.");
      window.setTimeout(() => setDownloadError(""), 2600);
    }
  };

  const previewAttachment = async (file) => {
    if (!file?.id) return;
    try {
      setDownloadError("");
      setPreviewLoading(true);
      if (isTablePreview(file)) {
        const rawUrl = file.url || `/chat/files/${file.id}`;
        const apiPath = rawUrl.replace(/^https?:\/\/[^/]+\/api\//i, "/").replace(/^\/api\//, "/");
        const response = await api.get(`${apiPath}/preview`);
        setPreview((current) => {
          if (current?.url) URL.revokeObjectURL(current.url);
          return {
            mode: "table",
            name: file.name || "Attachment",
            columns: response.data?.columns || [],
            rows: response.data?.rows || [],
            file,
          };
        });
        return;
      }
      const response = await fetchAttachment(file);
      const type = response.headers?.["content-type"] || file.content_type || "application/octet-stream";
      const blob = new Blob([response.data], { type });
      const url = URL.createObjectURL(blob);
      setPreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return { mode: "blob", url, name: file.name || "Attachment", type, file };
      });
    } catch (_) {
      setDownloadError("Could not preview attachment. Please download it instead.");
      window.setTimeout(() => setDownloadError(""), 2600);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreview((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  const downloadBlobFromPreview = (item) => {
    if (!item?.url && item?.file) {
      downloadAttachment(item.file);
      return;
    }
    if (!item?.url) return;
    const link = document.createElement("a");
    link.href = item.url;
    link.download = item.name || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const fetchAttachment = (file) => {
    const rawUrl = file.url || `/chat/files/${file.id}`;
    const apiPath = rawUrl.replace(/^https?:\/\/[^/]+\/api\//i, "/").replace(/^\/api\//, "/");
    return api.get(apiPath, { responseType: "blob" });
  };

  const isTablePreview = (file = {}) => /\.(csv|xlsx|xls)$/i.test(file.name || "") || /spreadsheet|excel|csv/i.test(file.content_type || "");

  const markVisibleRead = useCallback(() => {
    if (!open || !isStaff || unreadMessages.length === 0) return;
    const messageIds = unreadMessages.map((message) => message.id).filter(Boolean);
    if (messageIds.length === 0) return;
    setMessages((current) => current.map((message) => {
      if (!messageIds.includes(message.id)) return message;
      return { ...message, read_by: Array.from(new Set([...readBy(message), String(user?.id || "")])) };
    }));
    api.post("/chat/messages/read", { message_ids: messageIds }).catch(() => {});
  }, [isStaff, open, unreadMessages, user?.id]);

  useEffect(() => {
    markVisibleRead();
  }, [markVisibleRead]);

  const messageReceipt = (message) => {
    if (message.sender_id !== user?.id) return null;
    if (message.scope !== "DIRECT") return null;
    const recipient = staff.find((member) => member.id === message.recipient_id);
    const seen = readBy(message).includes(String(message.recipient_id || ""));
    if (seen) return { icon: CheckCheck, className: "text-orange-400", label: "Seen" };
    if (recipient?.active || recipient?.seen_today) return { icon: CheckCheck, className: "text-zinc-400", label: "Delivered" };
    return { icon: Check, className: "text-zinc-500", label: "Sent" };
  };

  const startDrag = (event) => {
    const point = event.touches?.[0] || event;
    dragRef.current = {
      x: point.clientX,
      y: point.clientY,
      right: position.right ?? 24,
      bottom: position.bottom ?? 24,
      moved: false,
    };
    window.addEventListener("mousemove", moveDrag);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", moveDrag);
    window.addEventListener("touchend", stopDrag);
  };

  const moveDrag = (event) => {
    if (!dragRef.current) return;
    const point = event.touches?.[0] || event;
    if (Math.abs(point.clientX - dragRef.current.x) > 4 || Math.abs(point.clientY - dragRef.current.y) > 4) {
      dragRef.current.moved = true;
    }
    const nextRight = Math.max(8, dragRef.current.right - (point.clientX - dragRef.current.x));
    const nextBottom = Math.max(8, dragRef.current.bottom - (point.clientY - dragRef.current.y));
    setPosition({ right: nextRight, bottom: nextBottom });
  };

  const stopDrag = () => {
    if (dragRef.current?.moved) {
      suppressOpenRef.current = true;
      window.setTimeout(() => {
        suppressOpenRef.current = false;
      }, 0);
    }
    dragRef.current = null;
    window.removeEventListener("mousemove", moveDrag);
    window.removeEventListener("mouseup", stopDrag);
    window.removeEventListener("touchmove", moveDrag);
    window.removeEventListener("touchend", stopDrag);
  };

  if (!isStaff) return null;

  if (!open) {
    return (
      <button
        type="button"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        onClick={() => {
          if (suppressOpenRef.current) return;
          setOpen(true);
        }}
        className="fixed z-50 flex cursor-grab items-center gap-2 rounded-full border px-4 py-3 text-sm font-black text-white shadow-2xl shadow-black/40 active:cursor-grabbing"
        style={{
          right: position.right,
          bottom: position.bottom,
          borderColor: BRAND_ORANGE,
          background: "linear-gradient(135deg, #111827 0%, #09090b 58%, #1E2A44 100%)",
        }}
      >
        <Infinity size={20} style={{ color: BRAND_ORANGE }} />
        Loop
        {unreadCount > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs text-white">{unreadCount}</span>}
      </button>
    );
  }

  return (
    <section
      className="fixed z-50 w-[min(92vw,390px)] overflow-hidden rounded-[28px] border text-white shadow-2xl shadow-black/45"
      style={{
        right: position.right,
        bottom: position.bottom,
        borderColor: BRAND_ORANGE,
        background: "radial-gradient(circle at top left, rgba(242,106,33,0.18), transparent 34%), linear-gradient(145deg, #101827 0%, #09090b 62%, #1E2A44 100%)",
      }}
      aria-label="Internal staff chat"
    >
      <header
        className="flex cursor-grab items-center justify-between border-b border-white/10 bg-black/35 px-4 py-3 text-white active:cursor-grabbing"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      >
        <div className="flex items-center gap-2">
          <div>
            <p className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.18em]">
              <Infinity size={19} style={{ color: BRAND_ORANGE }} />
              Loop
              <span
                className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : "bg-zinc-500"}`}
                aria-label={connected ? "Connected" : "Reconnecting"}
              />
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setOpen(false)} className="rounded-full p-2 hover:bg-white/10" aria-label="Minimize chat">
            <Minus size={16} />
          </button>
          <button type="button" onClick={() => setOpen(false)} className="rounded-full p-2 hover:bg-white/10" aria-label="Close chat">
            <X size={16} />
          </button>
        </div>
      </header>

      <div className="grid max-h-[66vh] grid-rows-[auto_1fr_auto]">
        <div className="border-b border-white/10 bg-transparent px-4 py-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setRecipientOpen((value) => !value)}
              className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/15 bg-transparent px-3 py-2 text-left text-xs font-black text-white outline-none transition hover:border-orange-500/60"
              aria-expanded={recipientOpen}
              aria-label="Chat recipient"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${recipientId === "all" ? "bg-orange-400" : availability(selectedRecipient).dot}`} />
                <span className="truncate">
                  {recipientId === "all" ? "All staff" : `${safeName(selectedRecipient?.name)} · ${selectedRecipient?.role || "staff"}`}
                </span>
              </span>
              <ChevronDown size={15} className={`shrink-0 text-orange-400 transition ${recipientOpen ? "rotate-180" : ""}`} />
            </button>

            {recipientOpen && (
              <div className="mt-2 max-h-44 overflow-y-auto rounded-2xl border border-orange-500/30 bg-zinc-950/95 p-2 shadow-2xl shadow-black/40 backdrop-blur">
                <button
                  type="button"
                  onClick={() => {
                    setRecipientId("all");
                    setRecipientOpen(false);
                  }}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-bold transition hover:bg-white/10 ${recipientId === "all" ? "bg-orange-500/15 text-orange-200" : "text-zinc-100"}`}
                >
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />
                    All staff
                  </span>
                </button>
                {chatTargets.map((member) => {
                  const state = availability(member);
                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => {
                        setRecipientId(member.id);
                        setRecipientOpen(false);
                      }}
                      className={`mt-1 flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-xs font-bold transition hover:bg-white/10 ${recipientId === member.id ? "bg-orange-500/15 text-orange-200" : "text-zinc-100"}`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${state.dot}`} />
                        <span className="truncate">{safeName(member.name)} · {member.role}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div ref={listRef} className="space-y-3 overflow-y-auto px-4 py-4">
          {displayedMessages.map((message) => {
            const mine = message.sender_id === user?.id;
            const direct = message.scope === "DIRECT";
            const receipt = messageReceipt(message);
            const ReceiptIcon = receipt?.icon;
            return (
              <article key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`min-w-0 max-w-[82%] overflow-hidden rounded-2xl border px-3 py-2 shadow-sm ${mine ? "border-orange-500/50 bg-orange-500/15 text-white" : "border-white/10 bg-white/10 text-zinc-100"}`}>
                  <p className="mb-1 text-[10px] font-black uppercase tracking-[0.12em] opacity-70">
                    {mine ? "You" : safeName(message.sender_name)} · {message.sender_role || "staff"}
                  </p>
                  {direct && (
                    <p className={`mb-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${mine ? "bg-orange-500 text-white" : "bg-zinc-950 text-orange-300"}`}>
                      Private to {mine ? safeName(message.recipient_name) : "you"}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.message}</p>
                  {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                    <div className="mt-2 max-w-full space-y-1.5 overflow-hidden">
                      {message.attachments.map((file) => (
                        <div
                          key={file.id}
                          className={`flex w-full max-w-full min-w-0 items-center gap-2 overflow-hidden rounded-xl border px-2.5 py-2 text-left text-xs font-bold transition hover:border-orange-500/70 ${mine ? "border-orange-500/30 bg-black/15 text-orange-50" : "border-white/10 bg-black/20 text-zinc-100"}`}
                        >
                          <FileText size={15} className="shrink-0 text-orange-400" />
                          <span className="min-w-0 flex-1 truncate">{file.name || "Attachment"}</span>
                          <span className="max-w-[4rem] shrink-0 truncate text-[10px] text-zinc-400">{fileSize(file.size)}</span>
                          <button
                            type="button"
                            onClick={() => previewAttachment(file)}
                            className="shrink-0 rounded-full p-1.5 text-zinc-300 hover:bg-white/10 hover:text-orange-300"
                            aria-label={`Preview ${file.name || "attachment"}`}
                            title="Preview"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadAttachment(file)}
                            className="shrink-0 rounded-full p-1.5 text-zinc-300 hover:bg-white/10 hover:text-orange-300"
                            aria-label={`Download ${file.name || "attachment"}`}
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={`mt-1 flex items-center gap-1 text-[10px] font-bold ${mine ? "justify-end text-orange-100/70" : "justify-start text-zinc-400"}`}>
                    <span>{messageTime(message.created_at)}</span>
                  </div>
                  {ReceiptIcon && (
                    <div className="mt-1 flex justify-end">
                      <ReceiptIcon size={15} className={receipt.className} aria-label={receipt.label} />
                    </div>
                  )}
                </div>
              </article>
            );
          })}
          {displayedMessages.length === 0 && (
            <div className="rounded-2xl border border-dashed border-orange-500/40 bg-white/5 p-4 text-center text-sm font-semibold text-zinc-300">
              No Loop notes yet today. Ping someone with @name when the garage starts humming.
            </div>
          )}
        </div>

        <form onSubmit={send} className="border-t border-white/10 bg-black/30 p-3">
          {attachments.length > 0 && (
            <div className="mb-2 flex max-w-full flex-wrap gap-2 overflow-hidden">
              {attachments.map((file) => (
                <span key={file.id} className="inline-flex max-w-full min-w-0 items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-xs font-bold text-orange-100">
                  <FileText size={13} className="shrink-0" />
                  <span className="min-w-0 max-w-[190px] truncate">{file.name}</span>
                  <button type="button" onClick={() => setAttachments((current) => current.filter((item) => item.id !== file.id))} className="rounded-full p-0.5 hover:bg-white/10" aria-label="Remove attachment">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          {downloadError && (
            <p className="mb-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-100">
              {downloadError}
            </p>
          )}
          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-zinc-950/90 px-3 py-2 shadow-inner focus-within:border-orange-500/70">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={uploadFiles} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full p-1.5 text-orange-400 hover:bg-white/10 disabled:opacity-40" aria-label="Attach file">
              <Paperclip size={16} />
            </button>
            <AtSign size={16} className="text-orange-400" />
            <input
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={recipientId === "all" ? "Message all staff..." : `Private note to ${safeName(selectedRecipient?.name)}...`}
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-white outline-none placeholder:text-zinc-500"
              maxLength={1000}
            />
            <button
              type="submit"
              disabled={(!text.trim() && attachments.length === 0) || sending || uploading}
              className="rounded-full p-2 text-white disabled:opacity-40"
              style={{ backgroundColor: BRAND_ORANGE }}
              aria-label={uploading ? "Uploading file" : "Send chat message"}
            >
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
      {(preview || previewLoading) && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="flex h-[min(86vh,760px)] w-[min(94vw,900px)] flex-col overflow-hidden rounded-2xl border border-orange-500/50 bg-zinc-950 shadow-2xl shadow-black/60">
            <div className="flex items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-400">Attachment preview</p>
                <p className="truncate text-sm font-black text-white">{preview?.name || "Loading..."}</p>
              </div>
              <button type="button" onClick={closePreview} className="rounded-full p-2 text-zinc-300 hover:bg-white/10 hover:text-white" aria-label="Close preview">
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto bg-zinc-900">
              {previewLoading && <div className="grid h-full place-items-center p-8 text-sm font-bold text-zinc-400">Loading preview...</div>}
              {!previewLoading && preview?.mode === "table" && (
                <div className="min-w-full p-4">
                  <div className="overflow-auto rounded-xl border border-zinc-800 bg-zinc-950">
                    <table className="min-w-full border-collapse text-left text-xs text-zinc-100">
                      <thead className="sticky top-0 bg-zinc-900 text-orange-300">
                        <tr>
                          {(preview.columns || []).map((col, index) => (
                            <th key={`${col}-${index}`} className="border-b border-zinc-800 px-3 py-2 font-mono uppercase tracking-wider">{col || `Column ${index + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.rows || []).map((row, rowIndex) => (
                          <tr key={rowIndex} className="odd:bg-white/[0.03]">
                            {(preview.columns || row || []).map((_, colIndex) => (
                              <td key={colIndex} className="max-w-[240px] border-b border-zinc-900 px-3 py-2 align-top">{row?.[colIndex] || ""}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(preview.columns || []).length === 0 && <p className="p-6 text-center text-sm font-bold text-zinc-500">No preview rows found.</p>}
                  </div>
                </div>
              )}
              {!previewLoading && preview?.mode !== "table" && preview?.type?.startsWith("image/") && (
                <div className="grid min-h-full place-items-center p-4">
                  <img src={preview.url} alt={preview.name} className="max-h-full max-w-full object-contain" />
                </div>
              )}
              {!previewLoading && preview?.mode !== "table" && preview && !preview.type?.startsWith("image/") && (
                <iframe title={preview.name} src={preview.url} className="h-full min-h-[70vh] w-full border-0 bg-white" />
              )}
            </div>
            {preview && (
              <div className="flex justify-end border-t border-zinc-800 px-4 py-3">
                <button type="button" onClick={() => downloadBlobFromPreview(preview)} className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-xs font-black uppercase tracking-widest text-black hover:bg-orange-400">
                  <Download size={14} /> Download
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
